// FO-013 (batch-14) — min-uren als afhandelbaar Postvak In-item.
//
// Gedeelde Zod-schemas (API + form valideren met dezelfde bron) + DTOs voor de
// twee weergaven (per week + maandelijks gebundeld) en de afhandel-context in
// Postvak In. Afboeken is een EXPLICIETE keuze (of/waar), geen automatisch gevolg
// meer; de echte beslissing valt pas bij een maand die per saldo op min-uren
// uitkomt (koppeling met de FO-012 periode-saldering).

import { z } from 'zod'
import { PAYROLL_AFBOEK_EMMER, PAYROLL_AFBOEK_EMMER_LABELS, type PayrollAfboekEmmer } from './payroll-reservering'

export const MIN_UREN_STATUS = ['INGEDIEND', 'GOEDGEKEURD', 'AFGEKEURD', 'INGETROKKEN'] as const
export type MinUrenStatus = (typeof MIN_UREN_STATUS)[number]

export const MIN_UREN_STATUS_LABELS: Record<MinUrenStatus, string> = {
  INGEDIEND:   'Open',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD:   'Afgekeurd',
  INGETROKKEN: 'Vervallen',
}

// ─── Beoordeel-schemas (gedeeld, inline veldfouten) ───────────────────────────

/**
 * Goedkeuren met een EXPLICIETE afboek-keuze. `afboeken=false` (default) = alleen
 * akkoord, geen saldo-mutatie. `afboeken=true` + `doelEmmerType` = afboeken tegen
 * dat saldo (TVT/verlof); zonder `doelEmmerType` = registreren als debt-ledger
 * (MIN_UREN, geen verlof-emmer). Een emmer kiezen zonder `afboeken` is een
 * inconsistente keuze -> inline veldfout.
 */
export const minUrenGoedkeurenSchema = z
  .object({
    afboeken:      z.boolean().default(false),
    doelEmmerType: z.enum(PAYROLL_AFBOEK_EMMER).optional(),
    opmerking:     z.string().trim().max(2000).optional(),
  })
  .refine((d) => !d.doelEmmerType || d.afboeken, {
    message: 'Kies eerst "afboeken" voordat je een saldo selecteert.',
    path: ['doelEmmerType'],
  })
export type MinUrenGoedkeurenInput = z.infer<typeof minUrenGoedkeurenSchema>

export const minUrenAfkeurenSchema = z.object({
  opmerking: z.string().trim().max(2000).optional(),
})
export type MinUrenAfkeurenInput = z.infer<typeof minUrenAfkeurenSchema>

export const minUrenOverzichtQuerySchema = z.object({
  year:  z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
})
export type MinUrenOverzichtQueryInput = z.infer<typeof minUrenOverzichtQuerySchema>

// ─── DTOs: twee weergaven ─────────────────────────────────────────────────────

/** Eén min-uren-week (het signaal). */
export interface MinUrenWeekItemDTO {
  id: string
  employeeId: string
  employeeNumber: string
  naam: string
  year: number
  week: number
  uren: number
  status: MinUrenStatus
  reden: string | null
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
  /** Goedgekeurd MET afboeking (saldoMutatie aangemaakt). */
  afgeboekt: boolean
  /** De emmer waartegen is afgeboekt (of null = debt-ledger / geen afboeking). */
  afboekDoelEmmerType: PayrollAfboekEmmer | null
  /**
   * Of deze week om een beslissing vraagt: alleen wanneer de MAAND per saldo op
   * min-uren uitkomt (na periode-saldering). Intra-maand gecompenseerde weken
   * zijn `false` (informatief).
   */
  beslissingNodig: boolean
}

/** Maandelijks gebundeld per medewerker (het beslispunt). */
export interface MinUrenMaandBundelDTO {
  employeeId: string
  employeeNumber: string
  naam: string
  year: number
  month: number
  /** De weken (ISO) waarin een min-uren-signaal viel. */
  weken: number[]
  /** Som van de week-min-uren (bruto, vóór compensatie). */
  totaalWeekMinUren: number
  /**
   * Maand-net rest-tekort uit de FO-012 periode-saldering (100% + reisuren < norm).
   * > 0 = de maand komt per saldo op min-uren uit -> beslissing nodig.
   */
  maandNetTekortUren: number
  beslissingNodig: boolean
  /** Aantal nog open (INGEDIEND) week-verzoeken in deze maand. */
  openWeken: number
}

export interface MinUrenOverzichtDTO {
  year: number
  month: number
  weekItems: MinUrenWeekItemDTO[]
  maandBundels: MinUrenMaandBundelDTO[]
}

// ─── DTO: afhandel-context in Postvak In ──────────────────────────────────────

/** Eén beschikbaar saldo waartegen afgeboekt kan worden (met de actuele stand). */
export interface MinUrenAfboekEmmerDTO {
  type: PayrollAfboekEmmer
  label: string
  beschikbaarUren: number
}

/**
 * Context voor het afhandelen van één min-uren-week in Postvak In: het verzoek,
 * het maand-net beslispunt en de afboek-opties met het standaardvoorstel.
 */
export interface MinUrenContextDTO {
  verzoekId: string
  employeeId: string
  employeeNumber: string
  naam: string
  year: number
  week: number
  uren: number
  status: MinUrenStatus
  reden: string | null
  /** Maand-net rest-tekort (FO-012). > 0 = beslissing nodig. */
  maandNetTekortUren: number
  beslissingNodig: boolean
  /** Het standaardvoorstel voor de afboek-emmer (instelbaar; HR kiest bewust). */
  standaardAfboekEmmer: PayrollAfboekEmmer
  /** Beschikbare emmers + saldo, zodat HR/TL een geïnformeerde keuze maakt. */
  beschikbareEmmers: MinUrenAfboekEmmerDTO[]
}

/** Helper: label voor een afboek-emmer (re-export voor UI-gemak). */
export function minUrenEmmerLabel(emmer: PayrollAfboekEmmer): string {
  return PAYROLL_AFBOEK_EMMER_LABELS[emmer]
}
